// 360° feedback — rater view (Phase 4c). Pending requests to give feedback + a
// submit form.
import { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import { MessageSquare } from 'lucide-react';
import { Card, SectionTitle, CenterSpinner, PrimaryBtn, OutlineBtn, Pill } from './ui';
import { EmptyState } from './components';
import { AG } from './theme/tokens';
import { api } from './api';

function Stars({ value, onChange }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" onClick={() => onChange(n)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 24, lineHeight: 1, color: n <= value ? AG.primary : AG.n300 }}>★</button>
      ))}
    </div>
  );
}

export default function Feedback({ showToast }) {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.myFeedbackRequests().then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.rating || busy) return; setBusy(true);
    try { await api.submitFeedback(form.id, { rating: form.rating, strengths: form.strengths, improvements: form.improvements }); showToast?.('Feedback submitted'); setForm(null); load(); }
    catch (e) { showToast?.(e.message); } finally { setBusy(false); }
  };

  if (!rows) return <CenterSpinner />;
  const pending = rows.filter((r) => r.status === 'Pending' && r.cycleStatus === 'Open');
  const done = rows.filter((r) => r.status === 'Submitted');

  return (
    <div className="space-y-4 fade-up">
      <SectionTitle title="Feedback" sub="Feedback you've been asked to provide" />
      {pending.length === 0 && done.length === 0 ? (
        <Card><EmptyState icon={MessageSquare} title="No feedback requests" desc="When you're asked for 360° feedback it appears here." /></Card>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {pending.map((r) => (
            <Card key={r.id} style={{ padding: 14 }} className="flex items-center justify-between">
              <div>
                <div style={{ fontWeight: 600, color: AG.text }}>{r.subjectName} <span style={{ color: AG.n400, fontWeight: 400 }}>· {r.subjectRole}</span></div>
                <div style={{ fontSize: 12, color: AG.n500 }}>{r.title} · as {r.relation}</div>
              </div>
              <PrimaryBtn onClick={() => setForm({ id: r.id, subjectName: r.subjectName, relation: r.relation, rating: 0, strengths: '', improvements: '' })}>Give feedback</PrimaryBtn>
            </Card>
          ))}
          {done.map((r) => (
            <Card key={r.id} style={{ padding: 14 }} className="flex items-center justify-between">
              <div>
                <div style={{ fontWeight: 600, color: AG.text }}>{r.subjectName} <span style={{ color: AG.n400, fontWeight: 400 }}>· {r.title}</span></div>
                <div style={{ fontSize: 12, color: AG.n500 }}>as {r.relation} · rated {r.rating}/5</div>
              </div>
              <Pill tone="success">Submitted</Pill>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!form} onClose={() => !busy && setForm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Feedback · {form?.subjectName}</DialogTitle>
        <DialogContent>
          {form && (
            <div style={{ paddingTop: 6 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: AG.n600, marginBottom: 4 }}>Overall rating</div>
              <Stars value={form.rating} onChange={(rating) => setForm({ ...form, rating })} />
              <TextField fullWidth size="small" label="Strengths" multiline minRows={2} value={form.strengths} onChange={(e) => setForm({ ...form, strengths: e.target.value })} sx={{ mt: 2, mb: 1.5 }} />
              <TextField fullWidth size="small" label="Areas to improve" multiline minRows={2} value={form.improvements} onChange={(e) => setForm({ ...form, improvements: e.target.value })} />
            </div>
          )}
        </DialogContent>
        <DialogActions><OutlineBtn onClick={() => setForm(null)} disabled={busy}>Cancel</OutlineBtn><PrimaryBtn onClick={submit} disabled={busy || !form?.rating}>Submit</PrimaryBtn></DialogActions>
      </Dialog>
    </div>
  );
}
